import { TABLE_CART_EVENT_KIND } from "./tableCartEventPolicy";

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const sameId = (left, right) => {
  const normalizedLeft = normalizeId(left);
  const normalizedRight = normalizeId(right);
  return (
    normalizedLeft !== null &&
    normalizedRight !== null &&
    normalizedLeft === normalizedRight
  );
};

const matchesDeliveryEntry = (entry, event = {}) =>
  Boolean(
    entry?.isDelivery &&
      (sameId(entry?.id, event.tableId) ||
        sameId(entry?.tableId, event.tableId) ||
        sameId(entry?.originalTableId, event.tableId) ||
        sameId(entry?.orderId, event.orderId) ||
        sameId(entry?.deliveryOrder?.id, event.orderId)),
  );

export const resolveRealtimeTableContext = (
  event = {},
  { selectedTable = null, tables = [], clientTabs = [] } = {},
) => {
  if (typeof event.isDelivery === "boolean") {
    const selectedMatches =
      selectedTable &&
      (sameId(selectedTable.id, event.tableId) ||
        sameId(selectedTable.orderId, event.orderId) ||
        sameId(selectedTable.deliveryOrder?.id, event.orderId));
    const deliveryEntry = event.isDelivery
      ? (clientTabs || []).find((entry) =>
          matchesDeliveryEntry(entry, event),
        )
      : null;
    return {
      known: true,
      isDelivery: event.isDelivery,
      targetTableId:
        (selectedMatches ? selectedTable?.id : null) ??
        deliveryEntry?.id ??
        event.tableId ??
        null,
      source: "event",
    };
  }

  if (
    selectedTable &&
    (sameId(selectedTable.id, event.tableId) ||
      sameId(selectedTable.orderId, event.orderId) ||
      sameId(selectedTable.deliveryOrder?.id, event.orderId))
  ) {
    return {
      known: true,
      isDelivery: Boolean(selectedTable.isDelivery),
      targetTableId: selectedTable.id ?? event.tableId ?? null,
      source: "selection",
    };
  }

  const deliveryEntry = (clientTabs || []).find((entry) =>
    matchesDeliveryEntry(entry, event),
  );
  if (deliveryEntry) {
    return {
      known: true,
      isDelivery: true,
      targetTableId: deliveryEntry.id ?? event.tableId ?? null,
      source: "delivery-tabs",
    };
  }

  const physicalTable = (tables || []).find(
    (table) =>
      !table?.isDelivery &&
      (sameId(table?.id, event.tableId) ||
        sameId(table?.tableId, event.tableId)),
  );
  if (physicalTable) {
    return {
      known: true,
      isDelivery: false,
      targetTableId: physicalTable.id ?? event.tableId ?? null,
      source: "tables",
    };
  }

  return {
    known: false,
    isDelivery: null,
    targetTableId: event.tableId ?? null,
    source: "unknown",
  };
};

const uniqueValues = (values) =>
  Array.from(
    new Set(
      values
        .map((value) => normalizeId(value))
        .filter((value) => value !== null),
    ),
  );

export const mergeRealtimeTableIntent = (current = null, incoming = {}) => {
  const next = current || {
    hasDestructive: false,
    needsFullRefresh: false,
    needsIntegrityCheck: false,
    contextKnown: true,
    isDelivery: null,
    tableId: null,
    targetTableId: null,
    reasons: [],
    orderIds: [],
    orderItemIds: [],
  };
  const incomingKind = incoming.kind || null;
  const incomingContextKnown = incoming.contextKnown !== false;
  const contextsConflict =
    next.isDelivery !== null &&
    incoming.isDelivery !== null &&
    incoming.isDelivery !== undefined &&
    Boolean(next.isDelivery) !== Boolean(incoming.isDelivery);

  return {
    hasDestructive:
      next.hasDestructive ||
      incomingKind === TABLE_CART_EVENT_KIND.DESTRUCTIVE ||
      incoming.hasDestructive === true,
    needsFullRefresh:
      next.needsFullRefresh ||
      incoming.needsFullRefresh === true ||
      incomingKind === TABLE_CART_EVENT_KIND.POSITIVE ||
      incomingKind === TABLE_CART_EVENT_KIND.UNKNOWN,
    needsIntegrityCheck:
      next.needsIntegrityCheck || incoming.needsIntegrityCheck === true,
    contextKnown:
      next.contextKnown && incomingContextKnown && !contextsConflict,
    isDelivery:
      contextsConflict || !incomingContextKnown
        ? null
        : incoming.isDelivery !== null && incoming.isDelivery !== undefined
          ? Boolean(incoming.isDelivery)
          : next.isDelivery,
    tableId: incoming.tableId ?? next.tableId,
    targetTableId:
      contextsConflict || !incomingContextKnown
        ? null
        : incoming.targetTableId ?? next.targetTableId,
    reasons: uniqueValues([
      ...next.reasons,
      ...(incoming.reasons || []),
      incoming.reason,
    ]),
    orderIds: uniqueValues([
      ...next.orderIds,
      ...(incoming.orderIds || []),
      incoming.orderId,
    ]),
    orderItemIds: uniqueValues([
      ...next.orderItemIds,
      ...(incoming.orderItemIds || []),
      incoming.orderItemId,
    ]),
  };
};

export const createFixedWindowCoordinator = ({
  windowMs,
  merge = (current, incoming) => incoming ?? current,
  onFlush,
  schedule = (callback, delay) => setTimeout(callback, delay),
  cancel = (timer) => clearTimeout(timer),
} = {}) => {
  const entries = new Map();
  const delay = Math.max(0, Number(windowMs) || 0);

  const getEntry = (key) => {
    const normalizedKey = String(key);
    let entry = entries.get(normalizedKey);
    if (!entry) {
      entry = {
        key: normalizedKey,
        timer: null,
        batch: null,
        inFlight: false,
        pending: null,
      };
      entries.set(normalizedKey, entry);
    }
    return entry;
  };

  const arm = (entry) => {
    if (entry.timer || entry.inFlight || !entry.batch) return;
    entry.timer = schedule(() => {
      entry.timer = null;
      void flushEntry(entry);
    }, delay);
  };

  const flushEntry = async (entry) => {
    if (entry.inFlight || !entry.batch) return;
    const batch = entry.batch;
    entry.batch = null;
    entry.inFlight = true;
    try {
      await onFlush?.(entry.key, batch);
    } finally {
      entry.inFlight = false;
      if (entry.pending) {
        entry.batch = merge(entry.batch, entry.pending);
        entry.pending = null;
      }
      if (entry.batch) {
        arm(entry);
      } else if (!entry.timer) {
        entries.delete(entry.key);
      }
    }
  };

  const enqueue = (key, value) => {
    const entry = getEntry(key);
    if (entry.inFlight) {
      entry.pending = merge(entry.pending, value);
      return;
    }
    entry.batch = merge(entry.batch, value);
    arm(entry);
  };

  const isInFlight = (key) => Boolean(entries.get(String(key))?.inFlight);

  const clear = () => {
    entries.forEach((entry) => {
      if (entry.timer) cancel(entry.timer);
    });
    entries.clear();
  };

  return {
    clear,
    enqueue,
    isInFlight,
    pendingKeys: () => Array.from(entries.keys()),
  };
};

export const mergeMetadataTableIds = (current = null, incoming = []) => {
  const result = new Set(current || []);
  const incomingValues =
    incoming instanceof Set
      ? Array.from(incoming)
      : Array.isArray(incoming)
        ? incoming
        : [incoming];
  incomingValues.forEach((tableId) => {
    const normalized = normalizeId(tableId);
    if (normalized !== null) result.add(normalized);
  });
  return result;
};

export const chooseMetadataRefreshMode = (tableIds = [], threshold = 8) => {
  const ids = Array.from(
    new Set((tableIds || []).map(String).filter(Boolean)),
  );
  const targetedIds = ids.filter((id) => id !== "*");
  return {
    mode:
      ids.includes("*") || targetedIds.length > Math.max(0, Number(threshold) || 0)
        ? "global"
        : "targeted",
    tableIds: targetedIds,
  };
};
